import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { FiSearch, FiClock, FiArrowRight, FiLoader } from "react-icons/fi";
import { fetchBlogPosts } from "../../api";
import BlogCard, { BlogThumb, CategoryPill, CATEGORY_THEME, formatDate } from "./BlogCard";

const CATEGORY_KEYS = Object.keys(CATEGORY_THEME);

// The articles under a blog page's banner: the Blogs page lists all four categories (with a filter); a category page lists its own.
export default function BlogSection({ category = "" }) {
  const [posts, setPosts] = useState(null);       // null = loading
  const [failed, setFailed] = useState(false);
  const [filter, setFilter] = useState("");
  const [search, setSearch] = useState("");
  const active = category || filter;

  useEffect(() => {
    let alive = true;
    setPosts(null);
    setFailed(false);
    fetchBlogPosts(category ? { category } : {}).then((list) => alive && setPosts(list)).catch(() => alive && setFailed(true));
    return () => { alive = false; };
  }, [category]);

  const shown = useMemo(() => {
    const term = search.trim().toLowerCase();
    return (posts || []).filter((p) => (!active || p.category === active) && (!term || `${p.title} ${p.excerpt}`.toLowerCase().includes(term)));
  }, [posts, active, search]);

  const counts = useMemo(() => Object.fromEntries(CATEGORY_KEYS.map((k) => [k, (posts || []).filter((p) => p.category === k).length])), [posts]);
  const featured = !search.trim() && shown.length > 3 ? shown.find((p) => p.is_featured) || shown[0] : null;
  const rest = featured ? shown.filter((p) => p.id !== featured.id) : shown;

  return (
    <section className="bg-navy text-white py-12" aria-label="Blog articles">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="flex flex-wrap items-center justify-between gap-4 mb-8">
          <div className="flex flex-wrap gap-2" role="group" aria-label="Blog categories">
            {category ? (
              <Link to="/blogs" className="px-4 py-2 rounded-full text-sm font-semibold border border-navy-border text-gray-300 hover:border-accent-light">All blogs</Link>
            ) : (
              <button type="button" onClick={() => setFilter("")} aria-pressed={!filter} className={`px-4 py-2 rounded-full text-sm font-semibold border transition-colors ${!filter ? "bg-accent-light text-navy border-accent-light" : "border-navy-border text-gray-300 hover:border-accent-light"}`}>
                All <span className="opacity-60">({(posts || []).length})</span>
              </button>
            )}
            {CATEGORY_KEYS.map((key) => {
              const theme = CATEGORY_THEME[key];
              const on = active === key;
              const style = `px-4 py-2 rounded-full text-sm font-semibold border transition-colors ${on ? "bg-accent-light text-navy border-accent-light" : "border-navy-border text-gray-300 hover:border-accent-light"}`;
              return category ? (
                <Link key={key} to={`/${theme.page}`} className={style} aria-current={on ? "page" : undefined}>{theme.label}</Link>
              ) : (
                <button key={key} type="button" onClick={() => setFilter(on ? "" : key)} aria-pressed={on} className={style}>{theme.label} <span className="opacity-60">({counts[key]})</span></button>
              );
            })}
          </div>
          <label className="relative w-full sm:w-72">
            <span className="sr-only">Search articles</span>
            <FiSearch className="absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-500" size={16} />
            <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search articles..." className="w-full pl-10 pr-4 py-2.5 rounded-full bg-white/5 border border-navy-border text-sm placeholder:text-gray-500 focus:outline-none focus:border-accent-light" />
          </label>
        </div>

        {posts === null && !failed && <p className="flex items-center justify-center gap-2 text-gray-400 py-16"><FiLoader className="animate-spin" /> Loading articles...</p>}
        {failed && <p className="text-center text-gray-400 py-16">The articles could not be loaded. Please try again in a moment.</p>}
        {posts && shown.length === 0 && (
          <div className="text-center py-16">
            <p className="text-lg font-semibold">{search ? `No article matches "${search}".` : "New articles are on their way."}</p>
            <p className="text-gray-400 mt-2">{search ? "Try a shorter or different word." : "Check back soon."}</p>
          </div>
        )}

        {featured && (
          <article className="group grid md:grid-cols-2 rounded-3xl overflow-hidden bg-white/5 border border-navy-border hover:border-accent-light/60 transition-colors mb-8">
            <Link to={`/blog/${featured.slug}`} className="block aspect-[16/9] md:aspect-auto md:min-h-[320px] bg-navy-dark overflow-hidden" aria-label={featured.title} tabIndex={-1}>
              <BlogThumb post={featured} eager className="group-hover:scale-105 transition-transform duration-500" />
            </Link>
            <div className="p-7 md:p-10 flex flex-col justify-center">
              <div className="flex items-center gap-3 mb-4"><CategoryPill category={featured.category} /><span className="text-xs font-semibold text-amber-300 uppercase tracking-wider">Featured</span></div>
              <h2 className="text-2xl md:text-3xl font-extrabold leading-tight tracking-tight"><Link to={`/blog/${featured.slug}`} className="hover:text-accent-light transition-colors">{featured.title}</Link></h2>
              <p className="text-gray-300 leading-relaxed mt-4">{featured.excerpt}</p>
              <div className="flex flex-wrap items-center gap-x-5 gap-y-3 mt-6">
                <span className="flex items-center gap-1.5 text-sm text-gray-400"><FiClock size={14} /> {featured.reading_minutes} min read &middot; {formatDate(featured.published_at)}</span>
                <Link to={`/blog/${featured.slug}`} className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-brand-gradient text-white text-sm font-semibold shadow-md shadow-accent/20">Read the article <FiArrowRight /></Link>
              </div>
            </div>
          </article>
        )}

        {rest.length > 0 && <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">{rest.map((post) => <BlogCard key={post.id} post={post} />)}</div>}
      </div>
    </section>
  );
}
